import React, {
  FunctionComponent,
  Dispatch,
  SetStateAction,
  ChangeEvent,
  useState,
  useEffect,
  useRef,
  useContext,
} from "react";
import sepReasons from "./sepReasons.json";
import { EnrollmentFormProps2021 } from "./../EnrollmentFormPage";
import PlanInfo from "./../PlanInfo";
import BackNextButtons from "./../BackNextButtons";
import {
  useTranslate,
  dateFormatter,
  getValidationObject,
  EnrollmentPages,
  isValidDate,
  limitInputLengthCustomSelector,
} from "../../../helpers";
import {
  CollapsibleBox,
  FocusableHeader,
  ScrollTable,
  Tooltip,
} from "../../../components";
import { Choice, DateField, TextField, Alert } from "@cmsgov/ds-medicare-gov";
import { MCTDate, EnrollData2021 } from "../../../@types/index";
import { withRouter, RouteComponentProps } from "react-router";
import routes from "../../../app/routes";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";
import { useFlags } from "launchdarkly-react-client-sdk";
import { linkToSpecialEnrollmentPeriodInfo } from "../../../helpers/CONSTANTS";
import { AppContext } from "../../../app/store";
import { isWithinInterval } from "date-fns";

const SpecialEnrollmentChoice: FunctionComponent<{
  reason: {
    reasonCode: string;
    label: string;
    requiresDate: boolean;
    dateLabel?: string;
    alertLabel?: string;
    ldFlag?: string;
    showDates?: {
      start: { month: number; day: number; nextYear?: boolean };
      end: { month: number; day: number; nextYear?: boolean };
    };
    showAlertDates?: {
      start: { month: number; day: number; nextYear?: boolean };
      end: { month: number; day: number; nextYear?: boolean };
    };
    tooltip?: {
      triggerText: string;
      text: string;
      title: string;
    };
  };
  setEnrollData: Dispatch<SetStateAction<EnrollData2021>>;
  enrollData: EnrollData2021;
  formSubmitted: boolean;
}> = ({ reason, setEnrollData, enrollData, formSubmitted }) => {
  const t = useTranslate();
  const flags = useFlags();

  const {
    alertLabel,
    label,
    reasonCode,
    requiresDate,
    dateLabel,
    ldFlag,
    showDates,
    showAlertDates,
    tooltip,
  } = reason;

  let displayChoice = true;
  let showAlert = !!alertLabel;

  if (showAlert && showAlertDates) {
    const {
      month: startMonth,
      day: startDay,
      nextYear: startNextYear,
    } = showAlertDates.start;
    const {
      month: endMonth,
      day: endDay,
      nextYear: endNextYear,
    } = showAlertDates.end;
    const today = new Date();
    const start = new Date(
      today.getFullYear() + (startNextYear ? 1 : 0),
      startMonth - 1,
      startDay
    );
    const end = new Date(
      today.getFullYear() + (endNextYear ? 1 : 0),
      endMonth - 1,
      endDay
    );

    showAlert = isWithinInterval(today, { start, end });
  }

  if (ldFlag) {
    displayChoice = flags[ldFlag];
  } else if (showDates) {
    const {
      month: startMonth,
      day: startDay,
      nextYear: startNextYear,
    } = showDates.start;
    const {
      month: endMonth,
      day: endDay,
      nextYear: endNextYear,
    } = showDates.end;
    const today = new Date();
    const start = new Date(
      today.getFullYear() + (startNextYear ? 1 : 0),
      startMonth - 1,
      startDay
    );
    const end = new Date(
      today.getFullYear() + (endNextYear ? 1 : 0),
      endMonth - 1,
      endDay
    );

    displayChoice = isWithinInterval(today, { start, end });
  }

  const savedReason = enrollData.sepReasons.find(r => r.key === label);

  let dateErrorMessage = "";

  const monthName = `month.${label}`;
  const dayName = `day.${label}`;
  const yearName = `year.${label}`;

  if (
    formSubmitted &&
    savedReason &&
    savedReason.requiresDate &&
    (!savedReason.date || !isValidDate(savedReason.date))
  ) {
    dateErrorMessage = t("sep_form.enter_valid_date");
  }

  return displayChoice ? (
    <Choice
      type="checkbox"
      name={t(label)}
      className="ds-u-margin-bottom--2"
      value={reasonCode}
      uncheckedChildren={
        <div className="ds-u-margin-left--5">
          {tooltip && (
            <Tooltip
              inline
              triggerText={t(tooltip.triggerText)}
              title={t(tooltip.title)}
            >
              {t(tooltip.text)}
            </Tooltip>
          )}
        </div>
      }
      checkedChildren={
        <>
          {(showAlert || requiresDate || tooltip) && (
            <div className="ds-u-margin-left--5 ds-u-margin-bottom--4">
              {tooltip && (
                <Tooltip
                  triggerClassName={alertLabel ? "ds-u-margin-bottom--2" : ""}
                  inline
                  triggerText={t(tooltip.triggerText)}
                  title={t(tooltip.title)}
                >
                  {t(tooltip.text)}
                </Tooltip>
              )}

              {showAlert && alertLabel && (
                <Alert role="alert" hideIcon={true}>
                  {t(alertLabel)}
                </Alert>
              )}

              {requiresDate && dateLabel && (
                <DateField
                  dateFormatter={dateFormatter}
                  errorMessage={dateErrorMessage}
                  monthName={monthName}
                  dayName={dayName}
                  yearName={yearName}
                  onChange={(
                    _: ChangeEvent<HTMLInputElement>,
                    date: MCTDate
                  ): void => {
                    limitInputLengthCustomSelector(
                      `input[name='${monthName}']`,
                      2
                    );
                    limitInputLengthCustomSelector(
                      `input[name='${dayName}']`,
                      2
                    );
                    limitInputLengthCustomSelector(
                      `input[name='${yearName}']`,
                      4
                    );

                    setEnrollData({
                      ...enrollData,
                      sepReasons: enrollData.sepReasons.map(r => {
                        if (r.key === label) {
                          r.date = date;
                        }

                        return r;
                      }),
                    });
                  }}
                  label={t(dateLabel)}
                  hint={t("dob.hint")}
                  dayLabel={t("dob.day_label")}
                  monthLabel={t("dob.month_label")}
                  yearLabel={t("dob.year_label")}
                />
              )}
            </div>
          )}
        </>
      }
      checked={!!enrollData.sepReasons.find(r => r.key === label)}
      onChange={(): void => {
        const index = enrollData.sepReasons.findIndex(r => r.key === label);

        if (index >= 0) {
          setEnrollData({
            ...enrollData,
            sepReasons: enrollData.sepReasons.filter(r => r.key !== label),
          });
        } else {
          setEnrollData({
            ...enrollData,
            sepReasons: [
              ...enrollData.sepReasons,
              { code: reasonCode, key: label, requiresDate },
            ],
          });
        }
      }}
    >
      {t(label)}
    </Choice>
  ) : (
    <></>
  );
};

const SepEnrollments: FunctionComponent<EnrollmentFormProps2021 &
  RouteComponentProps> = ({
  enrollData,
  setEnrollData,
  nextPage,
  plan,
  inReview,
  history,
}) => {
  const t = useTranslate();
  const {
    state: { language },
  } = useContext(AppContext);

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formIsValid, setFormIsValid] = useState(false);
  const [reasonsValid, setReasonsValid] = useState(false);
  const [otherReasonValid, setOtherReasonValid] = useState(true);
  const [enrollmentTableExpanded, setEnrollmentTableExpanded] = useState(false);

  const alertDiv = useRef<HTMLDivElement>(null);

  const {
    initialEnrollment: ieReasons,
    specialEnrollment: seReasons,
    medicareAdvantagePlan: maReasons,
  } = sepReasons;

  const next = (): void => {
    const {
      isValid,
      sepOtherReasonValidity,
      sepReasonsValidity,
    } = getValidationObject(enrollData, plan, EnrollmentPages.Sep);

    setReasonsValid(!!sepReasonsValidity);
    setOtherReasonValid(!!sepOtherReasonValidity);
    setFormIsValid(isValid);
    setFormSubmitted(true);

    if (isValid) {
      nextPage();
    }
  };

  useEffect(() => {
    if (formSubmitted && !formIsValid) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [formSubmitted]);

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enoll_form.start_your_application")}
      />

      <div>
        <PlanInfo plan={plan} showRequiredFieldNote={false} />

        <p>
          <FormattedHTMLMessage id="enroll_form.enrollment_periods" />
        </p>

        <p>{t("enroll_form.cost_plan_enrollment_period")}</p>

        <CollapsibleBox
          label={
            enrollmentTableExpanded
              ? t("enroll_form.hide_expanded_enrollment_periods")
              : t("enroll_form.show_expanded_enrollment_periods")
          }
          onOpening={(): void => setEnrollmentTableExpanded(true)}
          onClosing={(): void => setEnrollmentTableExpanded(false)}
          titleClassname="EnrollmentFormPage__expandButton"
          caretClassname="EnrollmentFormPage__expandCaret"
        >
          <ScrollTable className="EnrollmentFormPage__sepTable">
            <thead>
              <tr>
                <th>{t("sep.2021.enrollment_period")}</th>
                <th>{t("sep.2021.you_can")}</th>
                <th>{t("sep.2021.coverage_starts")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-h5">
                    {t("sep.2021.open_enrollment_period")}
                  </p>
                  <p className="ds-u-margin-y--0">{t("sep.2021.oct15_dec7")}</p>
                </td>
                <td>
                  <span>{t("sep.2021.join_drop_switch")}</span>
                </td>
                <td>
                  <span>{t("sep.2021.jan1_next_year")}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-h5">{t("sep.2021.iep")}</p>
                  <p className="ds-u-margin-y--0">
                    {t("sep.2021.new_to_medicare")}
                  </p>
                  <p>{t("sep.2021.starts_3_months_before_medicare")}</p>
                </td>
                <td>
                  <span>{t("sep.2021.iep.join_any_plan")}</span>
                  <ul className="ds-u-margin-y--1">
                    <li className="ds-u-margin-bottom--1">
                      {t("sep.2021.iep.join_part_a_part_b")}
                    </li>
                    <li>{t("sep.2021.iep.join_drug_plan")}</li>
                  </ul>
                </td>
                <td>
                  <span>{t("sep.2021.iep.varies")}</span>
                  <ul className="ds-u-margin-y--1">
                    <li className="ds-u-margin-bottom--1">
                      <FormattedHTMLMessage id="sep.2021.iep.request_before" />
                    </li>
                    <li>
                      <FormattedHTMLMessage id="sep.2021.iep.request_after" />
                    </li>
                  </ul>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-h5">
                    {t("sep.2021.iep_new")}
                  </p>
                  <p className="ds-u-margin-y--0">
                    {t("sep.2021.iep_new.only")}
                  </p>
                  <p>{t("sep.2021.iep_new.three_moths")}</p>
                </td>
                <td>
                  <span>{t("sep.2021.iep_new.join")}</span>
                </td>
                <td>
                  <span>{t("sep.2021.iep_new.same_day")}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-h5">{t("sep.2021.mapd")}</p>
                  <p className="ds-u-margin-y--0">{t("sep.2021.mapd.only")}</p>
                  <ul className="ds-u-margin-y--1">
                    <li className="ds-u-margin-bottom--1">
                      {t("sep.2021.mapd.jan1_march31")}
                    </li>
                    <li>
                      <Tooltip
                        triggerText={t("sep.2021.mapd.first_three_months")}
                        inline
                        title={t("sep.2021.mapd.first_three_months.title")}
                      >
                        {t("sep.2021.mapd.first_three_months.text")}
                      </Tooltip>
                    </li>
                  </ul>
                </td>
                <td>
                  <ul className="ds-u-margin-y--0">
                    <li className="ds-u-margin-bottom--1">
                      {t("sep.2021.mapd.switch")}
                    </li>
                    <li>
                      <FormattedMessage
                        id="sep.2021.mapd.drop"
                        values={{
                          joinADrugPlan: (
                            <Tooltip
                              triggerText={
                                <FormattedHTMLMessage id="sep.2021.mapd.join_drug_plan" />
                              }
                              inline
                              title={t("sep.2021.mapd.join_drug_plan.title")}
                            >
                              <FormattedHTMLMessage id="sep.2021.mapd.join_drug_plan.text" />
                            </Tooltip>
                          ),
                        }}
                      />
                    </li>
                  </ul>
                </td>
                <td>
                  <span>{t("sep.2021.mapd.first_of_month")}</span>
                </td>
              </tr>
              <tr>
                <td>
                  <p className="ds-u-margin-y--0 ds-h5">{t("sep.2021.sep")}</p>
                  <p>{t("sep.2021.sep.certain_situations")}</p>
                </td>
                <td>
                  <span>{t("sep.2021.sep.join_switch")}</span>
                </td>
                <td>
                  <span>{t("sep.2021.sep.first_of_month")}</span>
                </td>
              </tr>
            </tbody>
          </ScrollTable>
          <p>
            <a
              href="https://www.medicare.gov/sign-up-change-plans/joining-a-health-or-drug-plan"
              rel="noopener noreferrer"
              target="_blank"
            >
              {t("sep.2021.learn_more_enrollment_periods")}
            </a>
          </p>
        </CollapsibleBox>

        <div className="EnrollmentFormPage__Sep2021">
          <h2 className="ds-u-margin-bottom--0">
            {t("sep_form.confirm_enroll_period")}
          </h2>
          <p className="ds-u-margin-top--1">
            {t("sep.2021.only_join_if_situation_matches")}
          </p>
          <p className="ds-u-margin-top--1">
            <FormattedHTMLMessage id="sep.2021.eligibility_note" />
          </p>

          <div ref={alertDiv}>
            {!formIsValid && formSubmitted && (
              <Alert
                className="ds-u-margin-top--2"
                heading={t("enroll_form.needs_your_attention")}
                variation="error"
              >
                {t("enroll_form.complete_required")}
              </Alert>
            )}
          </div>

          <p className="ds-u-margin-bottom--0 ds-u-margin-top--7">
            {t("sep.2021.check_all_that_apply")}
          </p>
          {!reasonsValid && formSubmitted && (
            <p className="ds-u-margin-bottom--0 ds-u-color--error">
              {t("sep_form.select_at_least_one")}
            </p>
          )}

          <h3 className="ds-h3 ds-u-margin-top--1">
            {t("sep.2021.initial_enrollment_period")}
          </h3>
          <div className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
            {ieReasons.map((reason, i) => (
              <SpecialEnrollmentChoice
                key={i}
                reason={reason}
                setEnrollData={setEnrollData}
                enrollData={enrollData}
                formSubmitted={formSubmitted}
              />
            ))}
          </div>

          <h3 className="ds-h3">{t("sep.2021.mapd_enrollment_period")}</h3>
          <div className="ds-u-border-bottom--1 ds-u-padding-bottom--2">
            {maReasons.map((reason, i) => (
              <SpecialEnrollmentChoice
                key={i}
                reason={reason}
                setEnrollData={setEnrollData}
                enrollData={enrollData}
                formSubmitted={formSubmitted}
              />
            ))}
          </div>

          <h3 className="ds-h3">{t("sep_form.special_enrollment_period")}</h3>
          <a
            href={linkToSpecialEnrollmentPeriodInfo(language)}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t("sep.2021.learn_more_sep")}
          </a>

          <h4 className="ds-u-margin-bottom--0 ds-u-margin-top--4">
            {t("sep_form.change_where_you_live")}
          </h4>
          <p className="ds-u-margin-top--1">
            {t("sep.2021.after_move_join_plan")}
          </p>
          {seReasons.changeLocation.map((reason, i) => (
            <SpecialEnrollmentChoice
              key={i}
              reason={reason}
              setEnrollData={setEnrollData}
              enrollData={enrollData}
              formSubmitted={formSubmitted}
            />
          ))}

          <h4 className="ds-u-margin-bottom--0 ds-u-margin-top--4">
            {t("sep_form.you_lose_coverage")}
          </h4>
          <p className="ds-u-margin-top--1">
            {t("sep.2021.two_months_current_coverage")}
          </p>
          {sepReasons.specialEnrollment.loseCoverage.map((reason, i) => (
            <SpecialEnrollmentChoice
              key={i}
              reason={reason}
              setEnrollData={setEnrollData}
              enrollData={enrollData}
              formSubmitted={formSubmitted}
            />
          ))}

          <h4 className="ds-u-margin-bottom--0 ds-u-margin-top--4">
            {t("sep.2021.medicare_and_medicaid")}
          </h4>
          <p className="ds-u-margin-top--1">
            {t("sep.2021.two_months_medicaid")}
          </p>
          {sepReasons.specialEnrollment.medicareMedicaid.map((reason, i) => (
            <SpecialEnrollmentChoice
              key={i}
              reason={reason}
              setEnrollData={setEnrollData}
              enrollData={enrollData}
              formSubmitted={formSubmitted}
            />
          ))}

          <h4 className="ds-u-margin-bottom--0 ds-u-margin-top--4">
            {t("sep_form.special_situations")}
          </h4>
          {seReasons.otherSpecialSituations.map((reason, i) => (
            <SpecialEnrollmentChoice
              key={i}
              reason={reason}
              setEnrollData={setEnrollData}
              enrollData={enrollData}
              formSubmitted={formSubmitted}
            />
          ))}
          {plan.overall_star_rating.rating >= 3 && (
            <SpecialEnrollmentChoice
              reason={{
                requiresDate: false,
                label: "sep_reason.2021.three_star",
                reasonCode: "LPI",
              }}
              setEnrollData={setEnrollData}
              enrollData={enrollData}
              formSubmitted={formSubmitted}
            />
          )}
          {plan.high_performing && (
            <SpecialEnrollmentChoice
              reason={{
                requiresDate: false,
                label: "sep_reason.five_star",
                reasonCode: "5ST",
              }}
              setEnrollData={setEnrollData}
              enrollData={enrollData}
              formSubmitted={formSubmitted}
            />
          )}
          <Choice
            type="checkbox"
            name={t("sep_reason.other")}
            value={"OTH"}
            checked={enrollData.hasSepOtherReason}
            onChange={(): void => {
              const otherLabel = "sep_reason.other";

              const index = enrollData.sepReasons.findIndex(
                r => r.key === otherLabel
              );

              if (index >= 0) {
                setEnrollData({
                  ...enrollData,
                  sepReasons: enrollData.sepReasons.filter(
                    r => r.key !== otherLabel
                  ),
                  hasSepOtherReason: false,
                  sepOtherReason: "",
                });
              } else {
                setEnrollData({
                  ...enrollData,
                  sepReasons: [
                    ...enrollData.sepReasons,
                    {
                      code: "OTH",
                      key: otherLabel,
                      requiresDate: false,
                    },
                  ],
                  hasSepOtherReason: true,
                });
              }
            }}
            checkedChildren={
              <div className="ds-u-margin-left--7">
                <TextField
                  errorMessage={
                    !otherReasonValid && t("sep_form.enter_a_reason")
                  }
                  name={t("sep_form.other_reason")}
                  label={t("sep_form.other_reason")}
                  onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                    setEnrollData({
                      ...enrollData,
                      sepOtherReason: e.currentTarget.value,
                    });
                  }}
                />
              </div>
            }
          >
            {t("sep_reason.other")}
          </Choice>

          <h4 className="ds-h4 ds-u-margin-top--7">
            {t("sep_form.nothing_matches_situation")}
          </h4>
          <p className="ds-u-margin-top--1">{t("sep.2021.contact_plan")}</p>
        </div>

        <BackNextButtons
          nextPage={next}
          previousPage={(): void => {
            history.push(routes.searchResults);
          }}
          inReview={inReview}
        />
      </div>
    </div>
  );
};

export default withRouter(SepEnrollments);
